import { Preferences } from '@capacitor/preferences';
import { isNativeApp } from 'helpers';
import { sessionKeys } from './storageKeys';

// The "NativeStorage" group reads and writes the same place cordova-plugin-nativestorage did, so values saved by the
// Cordova app survive the switch. That plugin stored every value JSON-encoded, so keep doing that.
const nativeStorageReady = isNativeApp ? Preferences.configure({ group: 'NativeStorage' }) : Promise.resolve();

const readNative = async (key) => {
  await nativeStorageReady;
  const { value } = await Preferences.get({ key });
  if (value === null) return null;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const writeNative = async (key, value) => {
  await nativeStorageReady;
  await Preferences.set({ key, value: JSON.stringify(value) });
};

const removeNative = async (key) => {
  await nativeStorageReady;
  await Preferences.remove({ key });
};

const storage = {
  local: {
    get: async key => localStorage.getItem(key),
    set: async (key, value) => localStorage.setItem(key, value),
    remove: async key => localStorage.removeItem(key),
  },
  native: {
    isAvailable: () => isNativeApp,
    get: readNative,
    set: writeNative,
    remove: removeNative,
  },
  secure: {
    // TODO...
  },
};

const getValue = async (key, quick = false) => {
  const token = await storage.local.get(key);

  // Skips backup. Needed for a quick retrieval during unload event.
  // During the unload event native calls no longer run.
  if (quick) {
    return token;
  }

  if (!token && storage.native.isAvailable()) {
    const backup = await storage.native.get(key);

    if (backup) {
      await storage.local.set(key, backup);
      return backup;
    }
  }

  return token;
};

const setValue = async (key, value) => {
  if (storage.native.isAvailable()) {
    await Promise.all([
      storage.local.set(key, value),
      storage.native.set(key, value), // Awaited so a force-close after setValue resolves can't drop the native backup
    ]);
    return;
  }

  return storage.local.set(key, value);
};

const removeValue = async (key) => {
  if (storage.native.isAvailable()) {
    return Promise.all([storage.local.remove(key), storage.native.remove(key)]); // Important to clear both!
  }

  return storage.local.remove(key);
};

const removeValues = keys => Promise.all(keys.map(removeValue));

const clearSession = () => removeValues(sessionKeys);

export default {
  setValue,
  getValue,
  removeValue,
  removeValues,
  clearSession,
};
